import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import {
  ShieldCheck,
  FileBadge2,
  Truck,
  History,
  ArrowRight,
  ArrowUpRight,
  Phone,
  Mail,
  CalendarDays,
  Clock3,
  MapPin,
} from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem, MaskedLine, EASE } from "../components/Reveal";
import { PhotoSlider } from "../components/PhotoSlider";
import { useProducts } from "../hooks/useProducts";
import { usePosts } from "../hooks/usePosts";
import { INDUSTRIES } from "../data/industries";
import { SITE, waLink, IMAGES } from "../data/site";

const TRUST = [
  { icon: ShieldCheck, label: "3M Channel Partner" },
  { icon: FileBadge2, label: "MSME Registered" },
  { icon: Truck, label: "PAN India Supply" },
  { icon: History, label: "Since 1972" },
];

const STATS = [
  { v: "50+", k: "Years of trade" },
  { v: "EST. 1972", k: "Founded in Delhi" },
  { v: "1.1–66kV", k: "Voltage classes" },
  { v: "24h", k: "NCR dispatch" },
];

const TiltCard = ({ children, className = "" }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 16 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 16 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <motion.div
      style={{ rotateX: rX, rotateY: rY, transformPerspective: 900 }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Hero = () => {
  const { data: CATEGORIES } = useProducts();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const heroCat = CATEGORIES[0] || CATEGORIES[1];

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-navy" data-testid="home-hero">
      <div className="bp-grid absolute inset-0" />
      <div
        className="absolute -right-40 top-1/3 h-[560px] w-[560px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #FF5500 0%, transparent 65%)" }}
      />
      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-36 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pb-28 lg:pt-44">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 sm:text-xs"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block h-2 w-2 bg-safety" />
            3M Authorised Channel Partner · Bhagirath Palace, Delhi
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
            className="mt-4 flex items-start gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 sm:text-xs"
            data-testid="hero-address-line"
          >
            <MapPin size={13} className="mt-0.5 shrink-0 text-safety" />
            {SITE.heroAddress}
          </motion.p>

          <h1 className="mt-7 font-display text-4xl font-black uppercase leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Universal Engineers —{" "}</MaskedLine>
            <MaskedLine delay={0.28}><span className="text-safety">Cable Jointing Kits Supplier,{" "}</span></MaskedLine>
            <MaskedLine delay={0.41}>Delhi</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            Universal Engineers Delhi has supplied cable jointing kits,
            terminations and accessories from 1.1kV to 66kV for fifty years —
            trusted by utilities, EPCs and traders across India, from the heart
            of Old Delhi's electrical trade.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/enquiry"
              data-testid="hero-rfq-button"
              className="group flex items-center gap-2.5 bg-safety px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-all hover:bg-white hover:text-navy"
            >
              Get a Quote
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/products"
              data-testid="hero-products-button"
              className="border border-slate-600 px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-safety hover:text-safety"
            >
              Explore Product Lines
            </Link>
          </motion.div>

          <Stagger className="mt-14 grid grid-cols-2 gap-6 border-t border-slate-800 pt-8 sm:grid-cols-4" delay={1}>
            {STATS.map((s) => (
              <StaggerItem key={s.k} data-testid="hero-stat">
                <p className="font-display text-2xl font-extrabold text-white sm:text-3xl">{s.v}</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">{s.k}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>

        <motion.div style={{ y: imgY }} className="relative hidden lg:block">
          <TiltCard className="relative">
            <div className="clip-notch relative">
              <img
                src="/img/hero-3m-solution.jpg"
                alt="3M QS-III splices, HDT heavy-wall polyolefin and shear-bolt connectors — complete cable jointing kits by 3M"
                className="aspect-square w-full bg-navy object-contain"
                data-testid="hero-product-image"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              <div className="absolute -left-px bottom-0 h-1 w-1/3 bg-safety" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6, ease: EASE }}
              className="absolute -right-8 -top-6 flex items-center gap-3 border border-slate-700 bg-navy/95 px-5 py-4 shadow-2xl backdrop-blur"
              data-testid="hero-partner-badge"
            >
              <ShieldCheck size={26} className="text-safety" />
              <div>
                <p className="font-display text-sm font-bold text-white">3M Channel Partner</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">Genuine supplies · Est. 1972</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.6, ease: EASE }}
              className="absolute -bottom-5 right-6 flex items-center gap-2 bg-safety px-4 py-2.5"
              data-testid="hero-stock-chip"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                Ready Stock · Dispatches in 24h
              </span>
            </motion.div>
          </TiltCard>
        </motion.div>
      </div>
    </section>
  );
};

const TrustStrip = () => (
  <section className="border-b border-slate-200 bg-white" data-testid="trust-strip">
    <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
      {TRUST.map((t, i) => (
        <Reveal key={t.label} delay={i * 0.07}
          className={`flex items-center gap-4 px-6 py-7 sm:px-8 ${i < 3 ? "lg:border-r" : ""} ${i % 2 === 0 ? "border-r border-slate-200 lg:border-r" : ""} ${i < 2 ? "border-b border-slate-200 lg:border-b-0" : ""}`}
        >
          <t.icon size={26} className="shrink-0 text-safety" strokeWidth={1.6} />
          <p className="font-display text-sm font-bold text-navy">{t.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const BentoCard = ({ c, className = "", big = false }) => (
  <Link
    to={`/products/${c.slug}`}
    data-testid={`category-card-${c.slug}`}
    className={`group relative flex h-full min-h-[240px] flex-col justify-end overflow-hidden bg-navy ${className}`}
  >
    <img
      src={c.image}
      alt={c.alt}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/35 to-transparent" />
    <div className="relative z-10 p-6 sm:p-7">
      <div className="mb-3 flex flex-wrap gap-1.5">
        {(c.voltages || []).slice(0, big ? 4 : 3).map((v) => (
          <span key={v} className="border border-white/25 bg-navy/60 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-white backdrop-blur-sm">
            {v}
          </span>
        ))}
      </div>
      <div className="flex items-end justify-between gap-3">
        <h3 className={`font-display font-extrabold tracking-tight text-white ${big ? "text-2xl sm:text-3xl" : "text-lg"}`}>
          {c.name}
        </h3>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/30 text-white transition-all duration-300 group-hover:border-safety group-hover:bg-safety">
          <ArrowUpRight size={16} />
        </span>
      </div>
      {big && <p className="mt-2.5 max-w-md text-sm leading-relaxed text-slate-300">{c.short}</p>}
    </div>
  </Link>
);

const ProductBento = () => {
  const { data: CATEGORIES } = useProducts();
  const cats = CATEGORIES.slice(0, 7);

  return (
    <section className="bg-slate-50 py-20 sm:py-24" data-testid="product-bento">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
              <span className="inline-block h-2 w-2 bg-safety" /> The Range
            </p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Cable jointing kits, heat shrink joints &amp;<br className="hidden sm:block" /> termination kits — every voltage class covered.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/products" data-testid="bento-view-all-link" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-navy transition-colors hover:text-safety">
              View all products <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <Stagger className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-3" delay={0.05}>
          <StaggerItem className="sm:col-span-2 lg:row-span-2">
            <BentoCard c={cats[0] || CATEGORIES[0]} big className="h-full min-h-[320px] sm:min-h-[420px]" />
          </StaggerItem>
          <StaggerItem><BentoCard c={cats[1] || CATEGORIES[1]} className="h-full" /></StaggerItem>
          <StaggerItem><BentoCard c={cats[2] || CATEGORIES[2]} className="h-full" /></StaggerItem>
          <StaggerItem><BentoCard c={cats[3] || CATEGORIES[3]} className="h-full" /></StaggerItem>
          <StaggerItem><BentoCard c={cats[4] || CATEGORIES[4]} className="h-full" /></StaggerItem>
          <StaggerItem className="sm:col-span-2"><BentoCard c={cats[5] || CATEGORIES[5]} className="h-full" /></StaggerItem>
          <StaggerItem className="sm:col-span-2"><BentoCard c={cats[6] || CATEGORIES[6]} className="h-full" /></StaggerItem>
        </Stagger>
      </div>
    </section>
  );
};

const IndustriesPreview = () => (
  <section className="border-y border-slate-200 bg-white py-20 sm:py-24" data-testid="industries-preview">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
            <span className="inline-block h-2 w-2 bg-safety" /> Who we supply
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
            From grid substations to rooftop solar.
          </h2>
        </div>
        <Link to="/industries" data-testid="industries-preview-link" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-navy transition-colors hover:text-safety">
          All industries <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <Stagger className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7" delay={0.05}>
        {INDUSTRIES.map((ind) => (
          <StaggerItem key={ind.title}>
            <Link
              to="/industries"
              data-testid={`industries-preview-tile-${ind.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex h-full flex-col gap-3 border border-slate-200 bg-white p-5 transition-all hover:-translate-y-1 hover:border-safety hover:shadow-lg"
            >
              <ind.icon size={22} className="text-navy transition-colors group-hover:text-safety" strokeWidth={1.7} />
              <p className="font-display text-sm font-bold leading-snug text-navy">{ind.title}</p>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="grain relative overflow-hidden bg-navy py-20 sm:py-24" data-testid="home-cta-band">
    <div className="bp-grid absolute inset-0" />
    <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
        <Reveal>
          <h2 className="font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            Need a quote within <span className="text-safety">12 hours?</span>
          </h2>
          <p className="mt-5 max-w-xl leading-relaxed text-slate-400">
            Send your enquiry now — our team responds by next working day,
            first half, with pricing and stock position.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-stretch">
          <Link to="/enquiry" data-testid="cta-band-enquiry-button" className="flex items-center justify-center gap-2.5 bg-safety px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-white hover:text-navy">
            Get a Quote <ArrowRight size={16} />
          </Link>
          <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="cta-band-whatsapp-button" className="flex items-center justify-center gap-2.5 border border-slate-600 px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-[#25D366] hover:text-[#25D366]">
            WhatsApp Us
          </a>
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-center lg:justify-start">
            <a href={SITE.phoneHref} data-testid="cta-band-phone-link" className="flex items-center justify-center gap-2 font-mono text-xs text-slate-400 transition-colors hover:text-safety">
              <Phone size={13} /> {SITE.phoneDisplay}
            </a>
            <a href={`mailto:${SITE.email}`} data-testid="cta-band-email-link" className="flex items-center justify-center gap-2 font-mono text-xs text-slate-400 transition-colors hover:text-safety">
              <Mail size={13} /> {SITE.email}
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const SEO_LOCAL_NCR = ["Delhi", "Noida", "Gurugram", "Ghaziabad", "Faridabad", "Greater Noida", "Gurgaon", "Bahadurgarh"];
const SEO_LOCAL_INDIA = ["Mumbai", "Pune", "Ahmedabad", "Chennai", "Bengaluru", "Hyderabad", "Kolkata", "Lucknow", "Kanpur", "Jaipur", "Surat", "Indore"];

const SeoLocal = () => (
  <section className="py-20 sm:py-24" data-testid="seo-local-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
          <span className="inline-block h-2 w-2 bg-safety" /> Coverage
        </p>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
          Delhi NCR's 3M cable jointing specialists — supplying every corner of India.
        </h2>
      </Reveal>
      <Stagger className="mt-8 grid gap-6 lg:grid-cols-2" delay={0.08}>
        <StaggerItem>
          <div className="h-full border border-slate-200 bg-white p-7" data-testid="coverage-ncr">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-safety">Same-day dispatch — Delhi NCR</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {SEO_LOCAL_NCR.map((c) => (
                <span key={c} className="border border-slate-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-700">{c}</span>
              ))}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Same-day dispatch on ready stock for Delhi, Noida, Gurugram, Ghaziabad, Faridabad
              and the wider NCR belt — site deliveries coordinated with your project schedule.
            </p>
          </div>
        </StaggerItem>
        <StaggerItem>
          <div className="h-full border border-slate-200 bg-white p-7" data-testid="coverage-india">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-safety">3–5 day supply — PAN India</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {SEO_LOCAL_INDIA.map((c) => (
                <span key={c} className="border border-slate-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-700">{c}</span>
              ))}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Dedicated transport partners carry 3M cable accessories to utilities, solar parks,
              metro projects and traders nationwide — 24–48 hours across North India.
            </p>
          </div>
        </StaggerItem>
      </Stagger>
    </div>
  </section>
);

const FAQS = [
  { q: "Are you an authorised 3M channel partner?", a: "Yes. Universal Engineers 3M authorized distributor & channel partner status covers 3M Electro & Communication Division — a partnership that began in 2007 when 3M took over Mahindra's Cable Jointing Division (M-SEAL), our partner since 1972." },
  { q: "Which voltage classes do you cover?", a: "Universal Engineers cable jointing kits, terminations and accessories cover 1.1kV to 66kV — including 11kV (UE), 33kV and 33kV (UE) classes — for LT and HT power cables." },
  { q: "Do you supply outside Delhi NCR?", a: "Yes — Universal Engineers Delhi supplies PAN India. Orders in Delhi NCR are dispatched the same day, North India receives deliveries in 24–48 hours, and the rest of India in 3–5 working days." },
  { q: "How quickly will I get a quote?", a: "Send your enquiry through the website, email or WhatsApp — our team responds with pricing and stock position by next working day, first half." },
  { q: "Where is Universal Engineers located?", a: "Universal Engineers Bhagirath Palace head office is at 1503, Electrical Market, Bhagirath Palace, Chandni Chowk, New Delhi — 110006, with a branch at 134, Patparganj Industrial Area, Delhi — 110092." },
  { q: "Do you provide installation support?", a: "Yes. Beyond supply, our certified technical teams provide end-to-end installation and site execution for LV and HT cable jointing and termination work." },
  { q: "Do you display prices online?", a: "We do not list public pricing. Share your requirement and quantity and we will respond with a tailored quotation by next working day, first half." },
];

const Faq = () => (
  <section className="border-t border-slate-200 bg-white py-20 sm:py-24" data-testid="faq-section">
    <div className="mx-auto max-w-4xl px-5 sm:px-8">
      <Reveal>
        <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
          <span className="inline-block h-2 w-2 bg-safety" /> Frequently Asked Questions
        </p>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
          Answers, before you even ask.
        </h2>
      </Reveal>
      <div className="mt-10 space-y-3">
        {FAQS.map((f, i) => (
          <Reveal key={f.q} delay={i * 0.04}>
            <details className="group border border-slate-200 bg-slate-50 p-5 open:bg-white" data-testid="faq-item">
              <summary className="flex cursor-pointer items-center justify-between gap-4 font-display text-base font-bold text-navy marker:content-['']">
                {f.q}
                <span className="font-mono text-safety transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
            </details>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const BlogPreview = () => {
  const { data: posts } = usePosts();
  const latest = (posts || []).slice(0, 3);
  if (!latest.length) return null;

  return (
    <section className="bg-slate-50 py-20 sm:py-24" data-testid="home-blog-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-safety">
              <span className="inline-block h-2 w-2 bg-safety" /> Blog &amp; Technical Guides
            </p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Field notes from five decades of jointing.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/blog" data-testid="home-blog-view-all" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-navy transition-colors hover:text-safety">
              All articles <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <Stagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" delay={0.04}>
          {latest.map((post) => (
            <StaggerItem key={post.slug}>
              <Link
                to={`/blog/${post.slug}`}
                data-testid={`home-blog-card-${post.slug}`}
                className="group flex h-full flex-col border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-safety hover:shadow-xl"
              >
                <div className="relative overflow-hidden bg-navy">
                  <img
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    className="aspect-[16/9] w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 bg-safety px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-white">
                    {post.tag || "Article"}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    <span className="flex items-center gap-1.5"><CalendarDays size={12} className="text-safety" /> {formatDate(post.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock3 size={12} className="text-safety" /> {post.readTime}</span>
                  </div>
                  <h3 className="mt-3 font-display text-lg font-bold leading-snug tracking-tight text-navy">{post.title}</h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{post.excerpt}</p>
                  <span className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-navy transition-colors group-hover:text-safety">
                    Read article <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

const Home = () => (
  <>
    <Seo
      title="Universal Engineers Delhi | Cable Jointing Kits Supplier | Bhagirath Palace"
      description="Universal Engineers – authorized distributor of cable jointing kits, heat shrink joints, LV/HT termination kits in Delhi. Buy online or contact us today."
      keywords="cable jointing kit, cable joint kit, heat shrink cable jointing kit, HT cable jointing kit, LV cable jointing kit, 11kV cable jointing kit, 33kV cable jointing kit, armoured cable joint kit, termination kit, cable jointing kit supplier Delhi, cable jointing kit manufacturer India"
      path="/"
      jsonld={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Universal Engineers",
        url: SITE.domain,
      }}
    />
    <Hero />
    <TrustStrip />
    <ProductBento />
    <PhotoSlider />
    <IndustriesPreview />
    <SeoLocal />
    <Faq />
    <BlogPreview />
    <CtaBand />
  </>
);

export default Home;
